import { parse } from "date-fns"

import { Avatar, styled } from "@l2r-front/l2r-ui"
import {
    formatDate,
    removeValueForArray,
} from "@l2r-front/l2r-utils"

import {
    ANTITAGS_FILTER,
    ATTACHMENT_TYPE_FILTER,
    AUTHOR_FILTER,
    DATE_FROM_FILTER,
    DATE_TO_FILTER,
    FILE_NAME_FILTER,
    FILE_TYPE_FILTER,
    OBJECT_TYPE_FILTER,
    TAGS_FILTER,
} from "./filters"
import { I18N_NAMESPACE } from "./i18n"
import { ModulesInfo } from "./modules"

export const documentsFiltersConfig = {
    [ATTACHMENT_TYPE_FILTER]: {
        getApiFilterValue: (value) => Array.isArray(value) ? value.join(",") : value,
        getValuesFromUrlParams: (value) => value ? value.split(",") : [],
        getUrlParams: (value) => Array.isArray(value) ? value.join(",") : value,
        getValueList: (key, values, t) => {
            return values.map((value, index) => ({
                key: key,
                index: index,
                label: t(I18N_NAMESPACE, ModulesInfo[value].labelKey),
                value: value,
            }))
        },
        removeValue: removeValueForArray,
        label: "forms.documentFiltersForm.inputs.attachmentType",
        queryParam: OBJECT_TYPE_FILTER,
    },
    [AUTHOR_FILTER]: {
        getApiFilterValue: (value) => Array.isArray(value) ? value.join(",") : value,
        getValuesFromUrlParams: (value) => value ? value.split(",") : [],
        getUrlParams: (value) => Array.isArray(value) ? value.join(",") : value,
        getValueList: (key, values, users) => {
            return values
                .filter(value => users.find(u => u.uuid.toString() === value.toString()))
                .map((value, index) => {
                    const user = users.find(u => u.uuid.toString() === value.toString())
                    const fullName = `${user?.first_name} ${user?.last_name}`
                    return ({
                        key: key,
                        icon: <StyledAvatar alt={fullName} src={user.avatar} size={24} />,
                        index: index,
                        label: fullName,
                        value: value,
                    })
                })
        },
        removeValue: removeValueForArray,
        label: "forms.documentFiltersForm.inputs.author",
        queryParam: "created_by",
    },
    [DATE_FROM_FILTER]: {
        getApiFilterValue: (value) => value ? formatDate(parse(value, "dd-MM-yyyy", new Date()), "yyyy-MM-dd") : null,
        getValuesFromUrlParams: (value) => value,
        getUrlParams: (value) => value,
        getValueList: (key, value) => {
            return ({
                key: key,
                label: value ? `≥ ${formatDate(parse(value, "dd-MM-yyyy", new Date()), " dd/MM/yyyy")}` : null,
                value: value,
            })
        },
        removeValue: () => null,
        label: "forms.documentFiltersForm.inputs.dateFrom",
        queryParam: "date__gte",
    },
    [DATE_TO_FILTER]: {
        getApiFilterValue: (value) => value ? formatDate(parse(value, "dd-MM-yyyy", new Date()), "yyyy-MM-dd") : null,
        getValuesFromUrlParams: (value) => value,
        getUrlParams: (value) => value,
        getValueList: (key, value) => {
            return ({
                key: key,
                label: value ? `≤ ${formatDate(parse(value, "dd-MM-yyyy", new Date()), " dd/MM/yyyy")}` : null,
                value: value,
            })
        },
        removeValue: () => null,
        label: "forms.documentFiltersForm.inputs.dateTo",
        queryParam: "date__lte",
    },
    [FILE_NAME_FILTER]: {
        getApiFilterValue: (value) => value,
        getValuesFromUrlParams: (value) => value,
        getUrlParams: (value) => value,
        getValueList: (key, value) => {
            return ({
                key: key,
                label: value,
                value: value,
            })
        },
        removeValue: () => "",
        label: "forms.documentFiltersForm.inputs.fileName",
        queryParam: "filename__contains",
    },
    [FILE_TYPE_FILTER]: {
        getApiFilterValue: (value) => Array.isArray(value) ? value.join(",") : value,
        getValuesFromUrlParams: (value) => value ? value.split(",") : [],
        getUrlParams: (value) => Array.isArray(value) ? value.join(",") : value,
        getValueList: (key, values) => {
            return values.map((value, index) => ({
                key: key,
                index: index,
                label: value.toString(),
                value: value,
            }))
        },
        removeValue: removeValueForArray,
        label: "forms.documentFiltersForm.inputs.documentType",
        queryParam: "document_type",
    },
    [ANTITAGS_FILTER]: {
        getApiFilterValue: (value) => Array.isArray(value) ? value.join(",") : value,
        getValuesFromUrlParams: (value) => value ? value.split(",") : [],
        getUrlParams: (value) => value,
        getValueList: (key, values) => {
            return values.map((value, index) => ({
                color: "action/darkRed",
                secondaryColor: "red/50",
                key: key,
                index: index,
                label: value.toString(),
                value: value,
            }))
        },
        removeValue: (filters, valueToRemove) => {
            return filters.filter(f => f !== valueToRemove.value)
        },
        label: "forms.documentFiltersForm.inputs.antiTags",
        queryParam: "anti_tag",
    },
    [TAGS_FILTER]: {
        getApiFilterValue: (value) => Array.isArray(value) ? value.join(",") : value,
        getValuesFromUrlParams: (value) => value ? value.split(",") : [],
        getUrlParams: (value) => value,
        getValueList: (key, values) => {
            return values.map((value, index) => ({
                key: key,
                index: index,
                label: value.toString(),
                value: value,
            }))
        },
        removeValue: (filters, valueToRemove) => {
            return filters.filter(f => f !== valueToRemove.value)
        },
        label: "forms.documentFiltersForm.inputs.tags",
        queryParam: "tags",
    },
}

const StyledAvatar = styled(Avatar)(({ theme }) => ({
    border: `1px solid ${theme.palette.primary.main} !important`,
    backgroundColor: "white",
    color: `${theme.palette.primary.main} !important`,
}))